import { ref, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import VueDatePicker from '@vuepic/vue-datepicker'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import FormInputRichText from 'components/FormInputRichText/FormInputRichText.vue'
import danishPhoneValidator from '../../foundation/js/inputValidators/danishPhoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import textAreaValidator from '../../foundation/js/inputValidators/textAreaValidator'
import postalCodeValidator from 'foundation/js/inputValidators/postalCodeValidator'
import { scrollToFirstError } from '../../foundation/js/inputValidators/scrollToFirstError'
import stripHtml from '../../foundation/js/stripHtml'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import { FUNDRAISING_TARGET_FIELD_ID } from 'foundation/js/utils/fundraisingFormAnchors'

// Room left above the goal field so its label stays in view.
const FIELD_SCROLL_MARGIN = 200

export default {
    name: 'PersonalFundraising',
    components: {
        VueDatePicker,
        FormInputTextField,
        FormInputRichText,
        TooltipComponent
    },
    props: {
        isMindegave: {
            type: Boolean,
            required: false,
            default: false
        },
        isUpdate: {
            type: Boolean,
            required: false,
            default: false
        },
        personalFundraising: {
            type: Object,
            required: true
        },
        showUnsavedChangesWarning: {
            type: Boolean,
            required: false,
            default: false
        },
        userData: {
            type: Object,
            required: true
        },
        privacyPolicyEncoded: {
            type: String,
            required: false,
            default: ''
        },
        showUserConsent: {
            type: Boolean,
            required: false,
            default: false
        },
        showPostalCodeInput: {
            type: Boolean,
            required: false,
            default: false
        },
        hideFundraisingTarget: {
            type: Boolean,
            required: false,
            default: false
        },
        hideFundraisingEndDate: {
            type: Boolean,
            required: false,
            default: false
        },
        showFundraiseCard: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const store = useStore()
        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'personalFundraising',
                labelKey
            })
        const headline = ref(props.personalFundraising?.Headline ?? '')
        const dateOfbirth = ref(props.personalFundraising?.DateOfBirth)
        const fundraisingTarget = ref(
            props.personalFundraising?.FundraisingTarget ?? ''
        )
        const dateOfdeath = ref(props.personalFundraising?.DateOfDeath)
        const endDate = ref(props.personalFundraising?.EndDate ?? '')
        const fundraiseReason = ref(
            props.personalFundraising?.FundraiseReason ?? ''
        )
        const fundraisePersonDescription = ref(
            props.personalFundraising?.FundraisePersonDescription ?? ''
        )
        const personalStoryImage = ref(
            props.personalFundraising?.personalStoryImages ?? []
        )
        const postalCode = ref(props.personalFundraising?.PostalCode ?? '')
        const postalCodeValid = ref(true)

        const fundraiseImage = ref(props.personalFundraising?.fundraiseImage)
        const datePickerRefDateOfBirth = ref()
        const datePickerRefDateOfDeath = ref()
        const datePickerRefEndDate = ref()
        const notificationSms = ref(
            props.personalFundraising?.SmsNotification ?? false
        )
        const notificationEmail = ref(
            props.personalFundraising?.EmailNotification ?? false
        )
        const isSubmitting = computed(
            () => store.state.fundraising.isSubmitting
        )

        const firstName = ref(props.userData.firstname)
        const lastName = ref(props.userData.lastname)
        const email = ref(props.userData.email)
        const phone = ref(props.userData.telephone)
        const isReadOnly = ref({
            firstName: !!props.userData.firstname,
            lastName: !!props.userData.lastname,
            email: true,
            phone: !!props.userData.telephone,
        })
        const isFirstNameValid = computed(() => isReadOnly.value.firstName || nameValidator(firstName.value))
        const isLastNameValid = computed(() => isReadOnly.value.lastName || nameValidator(lastName.value))
        const isPhoneValid = computed(() => isReadOnly.value.phone || !notificationSms.value || danishPhoneValidator(phone.value))

        const hasConsent = ref(!!props.personalFundraising?.FundraisePersonDescription)

        const displayError = ref(false)
        const privacyPolicyWrapper = ref(null)
       
        const showUnsavedChangesWarning = props.showUnsavedChangesWarning;
        const handleBeforeUnload = (e) => {
            if (store.state.fundraising.isEditing && showUnsavedChangesWarning) {
                e.preventDefault();
                e.returnValue = ''; 
            }
        };
        
        if (showUnsavedChangesWarning) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }
        

        const isUploadingFundraiseImage = computed(
            () => store.state.fundraising.isFundraiseImageUploading
        )
        const personalStoryImagesUploading = computed(
            () => store.state.fundraising.personalStoryImagesUploading
        )

        const selectEndDate = () => {
            datePickerRefEndDate.value.selectDate()
        }

        const selectDateOfBirth = () => {
            datePickerRefDateOfBirth.value.selectDate()
        }

        const selectDateOfDeath = () => {
            datePickerRefDateOfDeath.value.selectDate()
        }

        // v10+ passes the returned style through unconverted, so these must be
        // CSS lengths rather than bare numbers.
        const customPosition = () => ({ top: '10px', left: '40px' })

        const headlineFilled = computed(() => {
            return headline.value.length > 2 &&
                      headline.value.length <= 75 &&
                      nameValidator(headline.value)
        })

        const dateOfbirthFilled = computed(() => {
            return props.isMindegave ? dateOfbirth.value : true
        })

        const endDateFilled = computed(() => {
            return props.hideFundraisingEndDate || endDate.value
        })

        const dateOfdeathFilled = computed(() => {
            return props.isMindegave ? dateOfdeath.value : true
        })

        const fundraisingTargetFilled = computed(() => {
            return props.hideFundraisingTarget || fundraisingTarget.value >= 100
        })

        const fundraiseReasonFilled = computed(() => {
            const text = stripHtml(fundraiseReason.value)
            return text.length > 2 && text.length <= 2000
        })

        const fundraisePersonDescriptionFilled = computed(() => {

            if (props.isMindegave) {
                return fundraisePersonDescription.value
                    ? fundraisePersonDescription.value.length > 2 &&
                        fundraisePersonDescription.value.length <= 2000 &&
                        textAreaValidator(fundraisePersonDescription.value)
                    : true
            }

            if (hasConsent.value) {
                return !!fundraisePersonDescription.value
            }

            return true
        })

        const personalStoryImageFilled = computed(() => {
            return !!personalStoryImage.value
        })

        const fundraiseImageFilled = computed(() => {
            return fundraiseImage.value
        })

        const pageId = computed(() => store.state.main.page_id)
        const isFundraiseImageUploadError = computed(
            () => store.state.fundraising.isFundraiseImageUploadError
        )
        const isPersonalStoryImageUploadError = computed(
            () => store.state.fundraising.isPersonalStoryImageUploadError
        )

        const postalCodeChanged = (newPostalCode) => {
            postalCodeValid.value = postalCodeValidator(newPostalCode)
        }

        const submit = function () {
            store.commit('fundraising/set_fundraise_image_upload_error', false)
            store.commit(
                'fundraising/set_personal_story_image_upload_error',
                false
            )

            const payload = {
                content: {
                    pageId: pageId.value,
                    headline: headline.value,
                    dateOfBirth: dateOfbirth.value,
                    dateOfDeath: dateOfdeath.value,
                    fundraisingTarget: props.hideFundraisingTarget ? 100 : parseInt(fundraisingTarget.value),
                    endDate: props.hideFundraisingEndDate ? null : endDate.value,
                    fundraiseDescription: stripHtml(fundraiseReason.value),
                    fundraisePersonDescription: (props.isMindegave || hasConsent.value)
                        ? fundraisePersonDescription.value || null
                        : null,
                    coverImage: fundraiseImage.value,
                    images: personalStoryImage.value,
                    notificationSms: notificationSms.value,
                    notificationEmail: notificationEmail.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    phone: phone.value || null,
                    postalCode: postalCode.value,
                    city: null // Adressevælger has no postal code lookup endpoint, so the city name is not looked up.
                },
                id: props.personalFundraising.Id
            }
            if (props.isUpdate)
                store.dispatch('fundraising/updatePersonalfundraising', payload)
            else
                store.dispatch('fundraising/createPersonalfundraising', payload)
        }

        const fundraiseImageUploader = async function (e) {
            const imageData = await store.dispatch(
                'fundraising/uploadFundraiseImage',
                {
                    type: 'fundraiseImage',
                    file: e.target.files[0],
                    id: props.personalFundraising.Id
                }
            )
            if (imageData) fundraiseImage.value = imageData
        }

        const personalStoryImageUploader = async function (e) {
            for (var file of e.target.files)
                singlePersonalStoryImageUploader(file)
        }

        const singlePersonalStoryImageUploader = async function (file) {
            const imageData = await store.dispatch(
                'fundraising/uploadFundraiseImage',
                {
                    type: 'personalStoryImage',
                    file: file,
                    id: props.personalFundraising.Id
                }
            )
            if (imageData) personalStoryImage.value.push(imageData)
        }

        const discardUploadedImage = function (imageType, discardFileId) {
            if (imageType === 'fundraiseImage') fundraiseImage.value = null
            if (imageType === 'personalStoryImage')
                personalStoryImage.value = personalStoryImage.value.filter(
                    (image) => image.id !== discardFileId
                )
        }

        const previousButtonPressed = () => {

            if (history.length <= 1) document.location = defaultRedirectUrl

            const backSteps = document.referrer && document.referrer.toLowerCase().includes('b2clogin.com') ? -2 : -1;
            history.go(backSteps);
        }

        const isCurrentStepValid = async () => {
            return (
                isFirstNameValid.value &&
                isLastNameValid.value &&
                isPhoneValid.value &&
                headlineFilled.value &&
                dateOfbirthFilled.value &&
                endDateFilled.value &&
                dateOfdeathFilled.value &&
                fundraisingTargetFilled.value &&
                fundraiseReasonFilled.value &&
                personalStoryImageFilled.value && 
                fundraisePersonDescriptionFilled.value &&
                postalCodeValid.value
            )
        }

        const nextButtonPressed = async () => {

            var isValid = await isCurrentStepValid();
            if (!isValid) {
                displayError.value = true
                scrollToFirstError('validation-error')
                return
            }
            
            displayError.value = false;
            submit();
        }

        const deleteButtonPressed = function () {
            if (window.confirm(getLabel('deleteConfirmation'))) {
                store.dispatch('fundraising/deletePersonalfundraising', {
                    id: props.personalFundraising.Id,
                    pageId: pageId.value
                })
            }
        }

        const formatFilesize = (fileSize) => {
            var roundedMb = (fileSize / Math.pow(1024, 2)).toFixed(0)
            if (roundedMb >= 1) return roundedMb + ' MB'
            var roundedKb = (fileSize / Math.pow(1024, 1)).toFixed(0)
            return roundedKb + ' KB'
        }

        const disableNextButton = computed(() => {
            return (
                isUploadingFundraiseImage.value ||
                personalStoryImagesUploading.value > 0
            )
        })

        const showPhoneInput = computed(() => {
            return notificationSms.value && !props.userData.telephone
        })

        // The browser has given up on the fragment by the time this form mounts.
        onMounted(async () => {
            if (
                window.location.hash.slice(1) !== FUNDRAISING_TARGET_FIELD_ID ||
                props.hideFundraisingTarget
            ) {
                return
            }

            await nextTick()
            const field = document.getElementById(FUNDRAISING_TARGET_FIELD_ID)
            if (!field) {
                return
            }

            // The group, so the label comes along. Two frames, so the form has
            // finished laying out before the offset is measured.
            const group =
                field.closest('.kb-personal-fundraising__input-wrapper') || field
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const top =
                        group.getBoundingClientRect().top +
                        window.scrollY -
                        FIELD_SCROLL_MARGIN
                    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
                    field.focus({ preventScroll: true })
                })
            })
        })

        return {
            discardUploadedImage,
            headline,
            dateOfbirth,
            fundraisingTarget,
            dateOfdeath,
            fundraiseReason,
            endDate,
            fundraisePersonDescription,
            personalStoryImage,
            fundraiseImage,
            fundraiseImageUploader,
            disableNextButton,
            headlineFilled,
            postalCodeValid,
            dateOfbirthFilled,
            fundraisingTargetFilled,
            dateOfdeathFilled,
            fundraiseImageFilled,
            personalStoryImageFilled,
            fundraiseReasonFilled,
            fundraisePersonDescriptionFilled,
            endDateFilled,
            getLabel,
            selectEndDate,
            datePickerRefEndDate,
            selectDateOfBirth,
            datePickerRefDateOfBirth,
            selectDateOfDeath,
            datePickerRefDateOfDeath,
            customPosition,
            personalStoryImageUploader,
            previousButtonPressed,
            nextButtonPressed,
            deleteButtonPressed,
            formatFilesize,
            isSubmitting,
            isUploadingFundraiseImage,
            personalStoryImagesUploading,
            isFundraiseImageUploadError,
            isPersonalStoryImageUploadError,
            notificationSms,
            notificationEmail,
            firstName,
            lastName,
            email,
            phone,
            isReadOnly,
            isFirstNameValid,
            isLastNameValid,
            isPhoneValid,
            displayError,
            privacyPolicyWrapper,
            hasConsent,
            showPhoneInput,
            postalCode,
            postalCodeChanged
        }
    }
}
